import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useAuth } from "../../context/AuthContext";
import { env } from "../../constants/env";
import ReusableTable from "../ReusableTable/ReusableTable";
import CommonButton from "../ui/CommonButton/CommonButton";
import InputField from "../ui/InputField/InputField";
import OverlayModal from "../ui/OverlayModal/OverlayModal";
import FileUpload from "../ui/FileUpload/FileUpload"; // make sure this exists
import { formatDate } from "../../utils/functions";
import classes from "./DistributionOrders.module.css";
import { useLocation } from "react-router-dom";

const DistributionOrders = () => {

  const location = useLocation();

  const { token, user, isAuthReady } = useAuth();
  const userRole = user?.role;

  const [distributionData, setDistributionData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [labs, setLabs] = useState([]);

  const [formData, setFormData] = useState({
    labId: "",
    kitType: "",
    quantity: "",
    trackingNumber: "",
    status: "Picked",
    dateSent: "",
    totalQuantityReceived: "",
    quantityReceivedGood: "",
    quantityReceivedBad: "",
    dateReceived: "",
    grnFile: null, // NEW: GRN file for LABORATORY
  });

  const statusOptions = [
    { label: "Picked", value: "Picked" },
    { label: "In Transit", value: "In Transit" },
    { label: "Received", value: "Received" },
    { label: "Partially Received", value: "Partially Received" },
    { label: "Completed", value: "Completed" },
  ];

  // ---------------- FETCH DATA ----------------
  const fetchOrders = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${env.kataApiUrl}/api/distribution-orders`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // const formatted = res.data.map((order) => ({
      //   ...order,
      //   dateSent: formatDate(order.dateSent),
      //   dateReceived: formatDate(order.dateReceived),
      // }));
      // setDistributionData(formatted);
    } catch (err) {
      toast.error("Failed to load distribution orders");
    } finally {
      setLoading(false);
    }
  };

  const fetchLabs = async () => {
    if (userRole !== "ZAMMSA" && userRole !== "OWNER") return;
    const res = await axios.get(`${env.kataApiUrl}/api/labs`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    setLabs(res.data.map((l) => ({ label: l.labName, value: l._id })));
  };

  useEffect(() => {
  if (!token || !user) return;
  // if (location.pathname === "/distribution-orders") {
    setDistributionData([]); // clear old data
    setLoading(true);
    fetchOrders(); // initial fetch

    const timer = setTimeout(async () => {
      try {
        const res = await axios.get(`${env.kataApiUrl}/api/distribution-orders`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const formatted = res.data.map((order) => ({
          ...order,
          dateSent: formatDate(order.dateSent),
          dateReceived: formatDate(order.dateReceived),
        }));
        setDistributionData(formatted); // only update data
      } catch (err) {
        console.log("Second fetch failed:", err);
      }
    }, 2000);

    return () => clearTimeout(timer);
  // }
}, [location.pathname, token, user]);

  useEffect(() => {
    // fetchOrders();
    fetchLabs();
  }, []);

  // ---------------- ACTIONS ----------------
  const handleAdd = () => {
    setEditingOrder(null);
    setFormData({
      labId: "",
      kitType: "",
      quantity: "",
      trackingNumber: "",
      status: "Picked",
      dateSent: "",
      totalQuantityReceived: "",
      quantityReceivedGood: "",
      quantityReceivedBad: "",
      dateReceived: "",
      grnFile: null,
    });
    setModalOpen(true);
  };

  const handleEdit = (row) => {
    setEditingOrder(row);
    setFormData({
      ...row,
      grnFile: null, // clear file input; existing URL is in grnFileUrl
    });
    setModalOpen(true);
  };

  const handleDelete = async (order) => {
    try {
      await axios.delete(
        `${env.kataApiUrl}/api/distribution-orders/${order.id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      toast.success("Order deleted");
      fetchOrders();
    } catch {
      toast.error("Failed to delete order");
    }
  };

  const canEditOrDelete = (order) => {
    if (!order) return false;
    if (userRole === "ZAMMSA") return order.status === "Picked";
    if (userRole === "LABORATORY" || userRole === "HEALTH CENTER")
      return order.status !== "Completed";
    return true;
  };

  // ---------------- SUBMIT ----------------
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitLoading(true);

    try {
      // Prepare FormData for file upload
      const payload = new FormData();
      Object.keys(formData).forEach((key) => {
        if (formData[key] !== undefined && formData[key] !== null) {
          // Only append file objects for the file input
          if (key === "grnFile") {
            if (formData.grnFile) payload.append("grnFile", formData.grnFile);
          } else {
            payload.append(key, formData[key]);
          }
        }
      });

      if (editingOrder) {
        await axios.put(
          `${env.kataApiUrl}/api/distribution-orders/${editingOrder.id}`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "multipart/form-data",
            },
          }
        );
        toast.success("Order updated");
      } else {
        await axios.post(`${env.kataApiUrl}/api/distribution-orders`, payload, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        });
        toast.success("Order created");
      }

      setModalOpen(false);
      fetchOrders();
    } catch (err) {
      toast.error(err.response?.data?.message || "Something went wrong");
    } finally {
      setSubmitLoading(false);
    }
  };

  // ---------------- RENDER ----------------
  return (
    <div className={classes.wrapper}>
      <ReusableTable
        title="ZAMMSA Distribution Orders"
        loading={loading}
        zammasTable={true}
        columns={[
          {
            label: "Order ID",
            field: "orderId",
            cellClassName: classes.noWrap,
            headCellClassName: classes.noWrap,
          },
          {
            label: "Send to Facility",
            field: "sendToFacility",
            cellClassName: classes.noWrap,
            headCellClassName: classes.noWrap,
          },
          {
            label: "Kit Type",
            field: "kitType",
            cellClassName: classes.noWrap,
            headCellClassName: classes.noWrap,
          },
          {
            label: "Quantity",
            field: "quantity",
            cellClassName: classes.noWrap,
            headCellClassName: classes.noWrap,
          },
          {
            label: "Tracking Number",
            field: "trackingNumber",
            cellClassName: classes.noWrap,
            headCellClassName: classes.noWrap,
          },
          {
            label: "Sent By",
            field: "sentBy",
            cellClassName: classes.noWrap,
            headCellClassName: classes.noWrap,
          },
          {
            label: "Date Sent",
            field: "dateSent",
            cellClassName: classes.noWrap,
            headCellClassName: classes.noWrap,
          },
          {
            label: "Total Quantity Received",
            field: "totalQuantityReceived",
            cellClassName: classes.noWrap,
            headCellClassName: classes.noWrap,
          },
          {
            label: "Good",
            field: "quantityReceivedGood",
            cellClassName: classes.noWrap,
            headCellClassName: classes.noWrap,
          },
          {
            label: "Bad",
            field: "quantityReceivedBad",
            cellClassName: classes.noWrap,
            headCellClassName: classes.noWrap,
          },
          {
            label: "Received By",
            field: "receivedBy",
            cellClassName: classes.noWrap,
            headCellClassName: classes.noWrap,
          },
          {
            label: "Date Received",
            field: "dateReceived",
            cellClassName: classes.noWrap,
            headCellClassName: classes.noWrap,
          },
          {
            label: "GRN File",
            field: "grnFileUrl",
            cellClassName: classes.noWrap,
            headCellClassName: classes.noWrap,
            cellRenderer: (row) =>
              row.grnFileUrl ? (
                <a
                  href={row.grnFileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={classes.downloadButton}
                >
                  View GRN File
                </a>
              ) : (
                "No file"
              ),
          },
          {
            label: "Status",
            field: "status",
            useStatusPill: false,
            cellClassName: classes.noWrap,
            headCellClassName: classes.noWrap,
          },
        ]}
        data={distributionData}
        showDetails={false}
        onAdd={userRole === "ZAMMSA" || userRole === "OWNER" ? handleAdd : null}
        showTopAdd={
          userRole === "ZAMMSA" ||
          (userRole === "OWNER" && distributionData.length > 0)
        }
        onEdit={(row) => {
          if (!canEditOrDelete(row)) {
            toast.info(
              userRole === "ZAMMSA"
                ? "Cannot edit this order because it is In Transit or beyond"
                : "Cannot edit this order because it is Completed"
            );
            return;
          }
          handleEdit(row);
        }}
        onDelete={(row) => {
          if (!canEditOrDelete(row)) {
            toast.info(
              "Cannot delete this order because it is In Transit or beyond"
            );
            return;
          }
          handleDelete(row);
        }}
        showActions={userRole !== "MINISTRY"}
      />

      {modalOpen && (
        <OverlayModal
          title={editingOrder ? "Edit Order" : "Create Order"}
          onClose={() => setModalOpen(false)}
        >
          <form
            onSubmit={handleSubmit}
            className="p-3 d-flex flex-column gap-3"
          >
            {(userRole === "ZAMMSA" || userRole === "OWNER") && (
              <>
                <InputField
                  labelText="Send to Facility"
                  dropdownOptions={labs}
                  value={formData.labId}
                  onChange={(e) =>
                    setFormData({ ...formData, labId: e.target.value })
                  }
                />

                <InputField
                  labelText="Kit Type"
                  value={formData.kitType}
                  onChange={(e) =>
                    setFormData({ ...formData, kitType: e.target.value })
                  }
                />

                <InputField
                  labelText="Quantity"
                  type="number"
                  value={formData.quantity}
                  onChange={(e) =>
                    setFormData({ ...formData, quantity: e.target.value })
                  }
                />

                <InputField
                  labelText="Tracking Number"
                  value={formData.trackingNumber}
                  onChange={(e) =>
                    setFormData({ ...formData, trackingNumber: e.target.value })
                  }
                />

                <InputField
                  labelText="Date Sent"
                  type="date"
                  value={formData.dateSent}
                  onChange={(e) =>
                    setFormData({ ...formData, dateSent: e.target.value })
                  }
                />
              </>
            )}

            {(userRole === "LABORATORY" || userRole === "HEALTH CENTER") && (
              <>
                <InputField
                  labelText="Total Quantity Received"
                  type="number"
                  value={formData.totalQuantityReceived}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      totalQuantityReceived: e.target.value,
                    })
                  }
                />

                <InputField
                  labelText="Quantity Received Good"
                  type="number"
                  value={formData.quantityReceivedGood}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      quantityReceivedGood: e.target.value,
                    })
                  }
                />

                <InputField
                  labelText="Quantity Received Bad"
                  type="number"
                  value={formData.quantityReceivedBad}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      quantityReceivedBad: e.target.value,
                    })
                  }
                />

                <InputField
                  labelText="Date Received"
                  type="date"
                  value={formData.dateReceived}
                  onChange={(e) =>
                    setFormData({ ...formData, dateReceived: e.target.value })
                  }
                />

                <div className="col-6">
                  <FileUpload
                    label="Upload GRN"
                    buttonText="Upload GRN"
                    onFileSelect={(file) =>
                      setFormData({ ...formData, grnFile: file })
                    }
                  />
                  {editingOrder && formData.grnFileUrl && (
                    <div className="mt-2">
                      <a
                        href={formData.grnFileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={classes.downloadButton}
                      >
                        View existing GRN
                      </a>
                    </div>
                  )}
                </div>
              </>
            )}

            <InputField
              labelText="Status"
              dropdownOptions={
                userRole === "ZAMMSA"
                  ? statusOptions.filter(
                      (o) => o.label === "Picked" || o.label === "In Transit"
                    )
                  : userRole === "LABORATORY" || userRole === "HEALTH CENTER"
                  ? statusOptions.filter(
                      (o) =>
                        o.label === "Received" ||
                        o.label === "Partially Received" ||
                        o.label === "Completed"
                    )
                  : statusOptions
              }
              value={formData.status}
              onChange={(e) =>
                setFormData({ ...formData, status: e.target.value })
              }
            />

            <CommonButton
              type="submit"
              disabled={submitLoading}
              title={submitLoading ? "Please wait..." : "Save"}
            />
          </form>
        </OverlayModal>
      )}
    </div>
  );
};

export default DistributionOrders;
